import React, { useRef, useState } from "react";
import { Icon, PlainButton, classnames } from "@culturehq/components";
import * as CHQ from "chq";

import { t } from "app/utils";
import eventTypes from "constants/eventTypes";

import styles from "./event-slider.module.scss";

type EventTypeDefaults = Omit<Partial<CHQ.Event>, "image"> & {
  image: {
    originalUrl: string;
  };
};

type EventType = {
  value: string;
  label: string;
  description: string;
  image: string;
  defaults: () => EventTypeDefaults;
};

type EventSliderProps = {
  onSetEventType: (_eventType: EventType) => void;
};

type EventTypeCardProps = EventSliderProps & {
  eventType: EventType;
};

const EventTypeCard: React.FC<EventTypeCardProps> = ({ eventType, onSetEventType }) => (
  <PlainButton
    className={styles.card}
    style={{ backgroundImage: `url(${eventType.image})` }}
    onClick={() => onSetEventType(eventType)}
  >
    <span className={styles.label}>{eventType.label}</span>
  </PlainButton>
);

const getSlideLayout = (
  index: number,
  containerRef: React.RefObject<HTMLDivElement>,
  sliderRef: React.RefObject<HTMLDivElement>
) => {
  let percent = index * (100 / eventTypes.length);
  const left = index !== 0;
  let right = index !== eventTypes.length - 1;

  const container = containerRef.current;
  const slider = sliderRef.current;

  if (!(container instanceof HTMLDivElement) || !(slider instanceof HTMLDivElement)) {
    return { percent, left, right };
  }

  const pixels = (percent * slider.clientWidth) / 100;
  const correction = pixels - (slider.clientWidth - container.clientWidth);

  if (correction > 0) {
    percent -= (correction / slider.clientWidth) * 100;
    right = false;
  }

  return { percent, left, right };
};

const EventSlider: React.FC<EventSliderProps> = ({ onSetEventType }) => {
  const [index, setIndex] = useState(0);

  const onNext = () => setIndex(value => value + 1);
  const onPrev = () => setIndex(value => value - 1);

  const containerRef = useRef<HTMLDivElement>(null);
  const sliderRef = useRef<HTMLDivElement>(null);
  const slideLayout = getSlideLayout(index, containerRef, sliderRef);

  return (
    <div style={{ position: "relative" }}>
      <button
        aria-label={t("Slider previous button")}
        type="button"
        className={classnames(styles.direction, styles.left)}
        onClick={onPrev}
        disabled={!slideLayout.left}
      >
        <Icon icon="chevron-left" />
      </button>
      <button
        aria-label={t("Slider next button")}
        type="button"
        className={classnames(styles.direction, styles.right)}
        onClick={onNext}
        disabled={!slideLayout.right}
      >
        <Icon icon="chevron-right" />
      </button>
      <div ref={containerRef} className={`${styles.container} no-scrollbar`}>
        <div
          ref={sliderRef}
          className={styles.slider}
          style={{ transform: `translateX(-${slideLayout.percent}%)` }}
        >
          {eventTypes.map(eventType => (
            <EventTypeCard
              key={eventType.value}
              eventType={eventType}
              onSetEventType={onSetEventType}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default EventSlider;
